// RegistrarTreinoCheckin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  logWorkoutStarted,
  logWorkoutCompleted,
  logXpGained,
} from "../../../services/analyticsService";
import { getAuth } from "firebase/auth";
import { Modal, Button, Form } from "react-bootstrap";
import Offcanvas from "../../../components/common/SwipeableOffcanvas.jsx";
import {
  getStorage,
  ref,
  uploadString,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import dayjs from "dayjs";
import BoxShadow from "../../../components/layout/BoxShadow.jsx";
import callFirebaseFunction from "../../../utils/firebaseFunctionCaller.js";
import Style from "./RegistrarTreinoCheckin.module.scss";
import {
  POINTS_CONFIG,
  calcPoints,
  estimarCalorias,
  ACTIVITIES,
  ACTIVITIES_BY_CATEGORY,
  ACTIVITY_CATEGORIES_ORDER,
} from "../utils/pointsConfig.js";
import { FaPlus, FaPencilAlt } from "react-icons/fa";
import { useUser } from "../../../contexts/UserContext.jsx";
import {
  CONVITE_SEM_COMPETICAO,
  pontosPrevistos,
} from "../../competicao/pontosDaCompeticao.js";
import { App } from "@capacitor/app";
import PublishButton from "./CheckinCelebration.jsx";
import WorkoutShare from "./WorkoutShare.jsx";
import { FiShare2 } from "react-icons/fi";
// Carrossel de compartilhar sob demanda: só quem toca no botão paga o Swiper.
const ShareRefeicao = React.lazy(() =>
  import("../../nutricao/components/ShareRefeicao")
);
import CustomCamera from "../../../components/ui/CustomCamera.jsx";
import { GeofenceService } from "../../../services/GeofenceService";
import { Capacitor } from "@capacitor/core";
import GeoPermissionDeniedBanner from "../../../components/geo/GeoPermissionDeniedBanner.jsx";
import {
  canShowGeoBanner,
  markGeoBannerShown,
} from "../../../services/geoBannerGate";
import StreakScreen from "../../streak/StreakScreen.jsx";
import { frozenWeekIndices } from "../../../utils/streakFreeze.js";
import {
  CHECKIN_MUSCLES,
  getActivityMusclePreset,
  sanitizeActiveMuscles,
} from "./muscle-map/activityMuscles.js";
import {
  prechecarCompeticao,
  criarEventoCompeticao,
} from "../../competicao/competicaoApi.js";
import { validarRegrasTreino } from "../../competicao/validacaoRegistroTreino.js";
import { lerMeusPontos } from "../../competicao/competicaoCache.js";
import { aplicarGanho } from '../../../services/moedaService';
import { campoTocavel } from '../../../utils/campoTocavel';
// Monetização: LevelUp → intersticial antes da ofensiva; VitaCoins no fim.
import { PassoPreOfensiva, PassoVitaCoins } from '../../monetizacao/PassosPosAcao';
import { anteciparDecisao } from '../../monetizacao/monetizacao';
import { celebracaoDoServidor } from '../../../config/celebrationPreview';

/**
 * Duração em segundos → rótulo curto ("45min", "1h20min").
 *
 * Fallback de exibição para atividades salvas sem `duration_display` (registros
 * antigos e os que vêm do VITA). Antes esta linha chamava duas funções que não
 * existiam no arquivo — o caminho quebrava com ReferenceError sempre que a
 * atividade tinha `duration_sec` e não tinha `duration_display`.
 */
const formatarDuracaoSeg = (segundos) => {
  const total = Math.max(0, Math.round(Number(segundos) || 0));
  if (!total) return "";
  const min = Math.round(total / 60);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const resto = min % 60;
  return resto ? `${h}h${resto}min` : `${h}h`;
};

const RegistrarTreinoCheckin = ({
  onSaved,
  editingCheckin,
  onCloseEdit,
  openTrigger,
  initialPhoto,
  livePrefill, // { title, atividades } — pré-preenchimento do treino ao vivo (Phase D)
  livePrefillTrigger, // muda p/ disparar a abertura pré-preenchida
  showFab = false, // FAB "+" agora vive no TreinoUser (instância única no root)
}) => {
  const storage = useMemo(() => getStorage(), []);
  const { user, profileData, vitaData, bodyScans } = useUser();

  /* Peso da última avaliação corporal — é o terceiro dado da fórmula de
     caloria (MET × peso × horas). Sem avaliação, `estimarCalorias` usa o peso
     padrão e marca o resultado como aproximado. */
  const pesoKg = useMemo(() => {
    const bruto = bodyScans?.[0]?.DADOS_BIOLOGICOS?.PESO;
    const n = Number(String(bruto ?? "").toString().replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [bodyScans]);
  const [saving, setSaving] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [showCustomCamera, setShowCustomCamera] = useState(false);
  // Voltar no passo 1 devolve à CÂMERA (é de onde o check-in nasce), mas
  // perguntando antes: o formulário já pode ter título, descrição e atividades
  // digitados, e sair sem aviso jogava tudo fora sem uma segunda chance.
  const [confirmarVoltar, setConfirmarVoltar] = useState(false);

  const [showCheckinOff, setShowCheckinOff] = useState(false);
  const [capturedImage, setCapturedImage] = useState(""); // dataUrl
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [checkinAt, setCheckinAt] = useState(
    dayjs().format("YYYY-MM-DDTHH:mm")
  );
  const [location, setLocation] = useState("");
  const [activitySearch, setActivitySearch] = useState("");
  const pickerBodyRef = useRef(null);
  /* RASCUNHO do que está sendo digitado nos campos numéricos, por
     "índice:campo". O estado da atividade guarda NÚMERO; o campo mostra o
     texto cru enquanto tem foco. Sem isto o <input type="number"> devolvia ""
     para qualquer valor intermediário que o WebKit não aceita ("1,", "0.",
     "1,5" no teclado pt-BR que só tem vírgula), o `Number("")` virava 0, o
     `value={a[field] || ""}` apagava o campo — e a pessoa não conseguia
     digitar nada em duração, calorias, distância e passos. */
  const [rascunhos, setRascunhos] = useState({});
  const lerRascunho = (idx, field, valorAtual) => {
    const chave = `${idx}:${field}`;
    if (Object.prototype.hasOwnProperty.call(rascunhos, chave)) return rascunhos[chave];
    return valorAtual ? String(valorAtual) : "";
  };
  const digitarNumero = (idx, field, texto) => {
    const limpo = String(texto).replace(/[^\d.,]/g, "");
    setRascunhos((r) => ({ ...r, [`${idx}:${field}`]: limpo }));
    const n = Number(limpo.replace(",", "."));
    updateActivity(idx, { [field]: Number.isFinite(n) ? n : 0 });
  };
  const encerrarRascunho = (idx, field) => {
    setRascunhos((r) => {
      const resto = { ...r };
      delete resto[`${idx}:${field}`];
      return resto;
    });
  };

  const vitaDataRef = useRef(vitaData);
  useEffect(() => {
    vitaDataRef.current = vitaData;
  }, [vitaData]);

  // ── Competição REGRAS_V2 ───────────────────────────────────────────────────
  // compAtiva: a academia do usuário tem competição com verificação ligada.
  // Quando true: a foto de um check-in NOVO vira OBRIGATÓRIA e só pela câmera
  // (sem galeria) e, após salvar, o app cria o evento de competição no
  // backend. Na edição a galeria volta (ver a CustomCamera lá embaixo).
  const [compAtiva, setCompAtiva] = useState(false);
  const [compWarning, setCompWarning] = useState(null); // {message, motivo}
  const compDecisionRef = useRef(null);

  useEffect(() => {
    let vivo = true;
    // Sonda única por montagem: backend responde SEM_COMPETICAO/REGRAS_OFF
    // quando a feature não vale para este usuário — tudo segue como hoje.
    prechecarCompeticao("TREINO")
      .then((res) => {
        if (!vivo) return;
        const silencioso =
          res?.motivo === "REGRAS_V2_DESLIGADA" ||
          res?.motivo === "SEM_COMPETICAO";
        setCompAtiva(res?.elegivel === true || !silencioso);
      })
      .catch(() => {
        /* backend fora do ar ⇒ fluxo antigo, sem competição */
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Pausa o handlePublish até o usuário decidir no aviso das regras
  // (v3.7.3 — Bloco E): o modal lista TODOS os motivos de não-pontuação e
  // resolve "salvar" | "editar" | "cancelar".
  const aguardarDecisaoRegras = (motivos) =>
    new Promise((resolve) => {
      compDecisionRef.current = resolve;
      setCompWarning({ motivos });
    });

  const responderAvisoRegras = (acao) => {
    setCompWarning(null);
    const resolve = compDecisionRef.current;
    compDecisionRef.current = null;
    if (resolve) resolve(acao);
  };

  // Campos a destacar quando o usuário escolhe "Ver e editar" no aviso.
  const [camposDestacados, setCamposDestacados] = useState([]);
  const destacado = (campo) => camposDestacados.includes(campo);
  const destaqueStyle = {
    border: "2px solid #E8A13A",
    borderRadius: 12,
    boxShadow: "0 0 0 3px rgba(232, 161, 58, 0.18)",
  };
  // ── Aviso de localização em CONTEXTO (v3.7.3 — Bloco C) ────────────────────
  // O lembrete de permissão parcial/negada só aparece aqui — ao registrar um
  // treino, que é quando a validação automática faria diferença — e no máximo
  // 1x a cada 14 dias (geoBannerGate). Nunca mais no cold start.
  const [geoBannerStatus, setGeoBannerStatus] = useState(null);
  const isGymLinkedGeo =
    user?.GYM_LINKED_PREMIUM === true ||
    user?.SUBSCRIPTION_INFO?.GYM_LINKED_PREMIUM === true ||
    user?.SUBSCRIPTION_INFO?.ASSINATURA_TYPE === "PARCEIRO" ||
    user?.PARTNER_USER === true;

  useEffect(() => {
    // editingCheckin direto (isEditMode é declarado mais abaixo no componente).
    if (!showCheckinOff || editingCheckin?.id) return;
    if (!Capacitor.isNativePlatform() || !isGymLinkedGeo) return;
    if (!canShowGeoBanner()) return;
    let vivo = true;
    GeofenceService.getPermissionStatus()
      .then(({ status }) => {
        if (!vivo) return;
        if (["whenInUse", "denied", "restricted"].includes(status)) {
          markGeoBannerShown(); // mostrar já conta o cooldown
          setGeoBannerStatus(status);
        }
      })
      .catch(() => {
        /* sem status, sem aviso */
      });
    return () => {
      vivo = false;
    };
  }, [showCheckinOff, editingCheckin]);

  // Phase D: marca se o check-in atual veio do treino AO VIVO (recebe bônus).
  const liveWorkoutRef = useRef(false);
  // Treino ao vivo finalizado e aguardando a foto: garante que, mesmo se o
  // usuário fechar a câmera sem tirar foto, o check-in pré-preenchido abra.
  const pendingLiveCheckinRef = useRef(false);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [atividades, setAtividades] = useState([]);

  // activity offcanvases
  const [showPickerOff, setShowPickerOff] = useState(false);
  const isEditMode = !!editingCheckin?.id;

  //streaks
  const [showStreak, setShowStreak] = useState(false);
  // Compartilhar o treino já registrado (aberto pelo botão flutuante do modo
  // edição — é a tela que mostra os dados daquele treino).
  const [shareRegistrado, setShareRegistrado] = useState(false);
  const streakDataRef = useRef(null);
  const streakTimeoutRef = useRef(null);
  const pendingShareRef = useRef(null);
  const [postWorkoutShare, setPostWorkoutShare] = useState(null);
  // Monetização: passo antes da ofensiva e a tela de VitaCoins no fim.
  const [preOfensiva, setPreOfensiva] = useState(null);
  const [vitaCoinsFim, setVitaCoinsFim] = useState(null);
  const moedaFimRef = useRef(null);
  const abrirVitaCoins = () => {
    const m = moedaFimRef.current;
    moedaFimRef.current = null;
    if (m?.creditou) setVitaCoinsFim(m);
  };

  // Limpa o timeout pendente da StreakScreen ao desmontar (evita memory leak)
  useEffect(
    () => () => {
      if (streakTimeoutRef.current) clearTimeout(streakTimeoutRef.current);
    },
    []
  );

  // quando vier edição, abre já preenchido
  useEffect(() => {
    if (!editingCheckin?.id) return;

    liveWorkoutRef.current = false; // edição de check-in manual: sem bônus ao vivo
    setShowCheckinOff(true);

    setTitle(editingCheckin?.title || "");
    setDescription(editingCheckin?.description || "");
    setLocation(editingCheckin?.location || "");

    // timestamp -> input datetime-local
    const ts = editingCheckin?.checkin_at;
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : typeof ts?._seconds === "number"
        ? new Date(ts._seconds * 1000)
        : new Date();

    setCheckinAt(dayjs(d).format("YYYY-MM-DDTHH:mm"));

    setAtividades(
      Array.isArray(editingCheckin?.atividades) ? editingCheckin.atividades : []
    );

    // não rebaixa foto pra dataURL; mantém vazia (só troca se usuário editar)
    setCapturedImage("");
  }, [editingCheckin]);

  useEffect(() => {
    const listener = App.addListener("appRestoredResult", (data) => {
      const pending = localStorage.getItem("pendingCameraAction");
      if (pending !== "checkin") return;
      if (data?.pluginId === "Camera" && data?.methodName === "getPhoto") {
        if (data?.success && data?.data?.dataUrl) {
          localStorage.removeItem("pendingCameraAction");
          // v3.7.3 (Bloco D): se a câmera matou o app DURANTE UMA
          // EDIÇÃO, o estado de edição se perdeu — reabrir aqui
          // abriria o formulário em modo CRIAÇÃO e o save duplicaria
          // o registro (era o "editar cria novo e pontua de novo").
          // Nesse caso descartamos a foto restaurada; o usuário
          // reabre a edição pela lista.
          if (localStorage.getItem("lesvita_editing_checkin_id")) {
            localStorage.removeItem("lesvita_editing_checkin_id");
            return;
          }
          setCapturedImage(data.data.dataUrl);
          setShowCheckinOff(true);
        }
      }
    });
    return () => {
      listener.then((l) => l.remove());
    };
  }, []);

  // Marca a edição em andamento de forma persistente (sobrevive ao restart do
  // processo causado pela câmera nativa) — consumida pelo listener acima.
  useEffect(() => {
    if (editingCheckin?.id) {
      localStorage.setItem(
        "lesvita_editing_checkin_id",
        String(editingCheckin.id)
      );
    } else {
      localStorage.removeItem("lesvita_editing_checkin_id");
    }
  }, [editingCheckin?.id]);

  // Dispara o fluxo de criação quando openTrigger mudar (via WorkoutDayCards)
  useEffect(() => {
    if (!openTrigger) return;
    openCreateFlow();
  }, [openTrigger]);

  // Phase D: ao FINALIZAR o treino ao vivo abre a CÂMERA primeiro e só então o
  // check-in PRÉ-PREENCHIDO (com a foto). A atividade vem de
  // buildPredictedAtividade(workout) → garante base==previsto. Se o usuário
  // fechar a câmera sem foto, o check-in abre mesmo assim (pendingLiveCheckinRef).
  // Geo/idempotência inalterados (server lê live_workout).
  useEffect(() => {
    if (!livePrefillTrigger || !livePrefill) return;
    resetAll();
    liveWorkoutRef.current = true;
    pendingLiveCheckinRef.current = true;
    setTitle(livePrefill.title || "Treino");
    setDescription(livePrefill.description || "");
    setAtividades(
      Array.isArray(livePrefill.atividades) ? livePrefill.atividades : []
    );
    setShowCustomCamera(true);
    // Dep intencionalmente só no trigger (livePrefill muda de ref a cada render).
  }, [livePrefillTrigger]);

  const requestId = useRef(null);
  const resetAll = () => {
    requestId.current = null;
    setFormStep(1);
    setCamposDestacados([]);
    setCapturedImage("");
    setTitle("");
    setDescription("");
    setCheckinAt(dayjs().format("YYYY-MM-DDTHH:mm"));
    setLocation("");
    setAtividades([]);
    setRascunhos({});
  };

  const openCreateFlow = () => {
    resetAll();
    liveWorkoutRef.current = false; // fluxo manual: nunca recebe bônus ao vivo
    if (initialPhoto) {
      setCapturedImage(initialPhoto);
      setShowCheckinOff(true);
    } else setShowCustomCamera(true);
    logWorkoutStarted(null, null);
  };
  const openChangePhoto = () => {
    setShowCustomCamera(true);
  };

  const openPicker = () => {
    setShowPickerOff(true);
  };

  const handleSelectActivity = (act) => {
    setAtividades((current) => [
      ...current,
      {
        activity_key: act.key,
        activity_label: act.label,
        category: act.category,
        start_time: dayjs().format("HH:mm"),
        duration_min: 0,
        distance_km: 0,
        calories: 0,
        steps: 0,
        points: 0,
        active_muscles: getActivityMusclePreset(act.key).muscles,
        config: POINTS_CONFIG[act.key] || POINTS_CONFIG.outro,
      },
    ]);
    setShowPickerOff(false);
    setActivitySearch("");
  };

  const updateActivity = (index, changes) => {
    setAtividades((current) =>
      current.map((activity, i) => {
        if (i !== index) return activity;
        const next = { ...activity, ...changes };
        if (Object.prototype.hasOwnProperty.call(changes, "duration_min")) {
          next.duration_sec = Number(next.duration_min) * 60;
          next.duration_display = formatarDuracaoSeg(next.duration_sec);
        }

        /* Caloria estimada a partir do que a pessoa JÁ informou: modalidade
           (MET) e duração. O campo continua editável — assim que alguém digita
           nele, `calories_manual` liga e a estimativa para de sobrescrever.
           Sem isto o campo ficava em branco, ia 0 para o banco, e a barra
           "Calorias da semana" somava zero com dias de treino marcados. */
        if (Object.prototype.hasOwnProperty.call(changes, "calories")) {
          next.calories_manual = true;
        } else if (!next.calories_manual) {
          next.calories = estimarCalorias({
            key: next.activity_key,
            durationMin: next.duration_min,
            pesoKg,
          }).kcal;
        }

        next.points = calcPoints({
          key: next.activity_key,
          durationMin: next.duration_min,
          distanceKm: next.distance_km,
          calories: next.calories,
          steps: next.steps,
        });
        return next;
      })
    );
  };

  const handlePickerScroll = (e) => {
    setShowScrollTop(e.target.scrollTop > 180);
  };

  const scrollPickerToTop = () => {
    pickerBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadPhotoIfNeeded = async () => {
    if (isEditMode && !capturedImage) {
      return {
        photo_url: editingCheckin?.photo_url || "",
        photo_path: editingCheckin?.photo_path || "",
      };
    }

    if (!capturedImage) return { photo_url: "", photo_path: "" };

    const uid = user?.uid || getAuth().currentUser?.uid;
    if (!uid) throw new Error("Usuário não autenticado.");

    const id = isEditMode ? editingCheckin.id : `${Date.now()}`;
    const path = `checkins_treino/${uid}/${id}.jpg`;
    const storageRef = ref(storage, path);

    // Sem contentType explícito: o SDK infere do próprio data URL (WebP/JPEG).
    await uploadString(storageRef, capturedImage, "data_url");
    const url = await getDownloadURL(storageRef);

    return { photo_url: url, photo_path: path };
  };

  const handlePublish = async () => {
    if (!title.trim()) throw new Error("Coloca um título pro seu check-in.");
    if (!atividades.length)
      throw new Error("Adiciona pelo menos 1 atividade pra publicar.");

    for (const activity of atividades) {
      const values = [
        activity.duration_min,
        activity.distance_km,
        activity.calories,
        activity.steps,
      ].map((value) => Number(value || 0));
      if (
        values.some((value) => !Number.isFinite(value) || value < 0) ||
        !values.some((value) => value > 0)
      ) {
        throw new Error(
          `Preencha duração, distância, calorias ou passos de ${activity.activity_label} com valores válidos.`
        );
      }
      if (
        getActivityMusclePreset(activity.activity_key).mode === "selectable" &&
        !sanitizeActiveMuscles(activity.active_muscles).length
      ) {
        throw new Error(
          `Selecione os grupos musculares de ${activity.activity_label}.`
        );
      }
    }

    // ── Avisos ANTES de registrar (o registro em si nunca é bloqueado).
    // Regra 26/08: XP de treino exige ≥40min E foto — vale para TODOS.
    // Quem está em competição vê a mensagem citando pontos também.
    let compElegivel = false;
    let compDuracao = 0;
    let compTipoAtividade = null;
    {
      // v3.7.3 (Bloco E): validação única (criação E edição) — o aviso lista
      // TODOS os motivos de não-pontuação e oferece "Ver e editar" com
      // destaque nos campos a ajustar. O registro em si nunca é bloqueado.
      const temFotoAtual = Boolean(
        capturedImage || (isEditMode && editingCheckin?.photo_url)
      );
      const validacao = await validarRegrasTreino({
        atividades,
        temFoto: temFotoAtual,
        compAtiva,
      });
      compDuracao = validacao.duracaoMin;
      compTipoAtividade = validacao.tipoAtividadeId;
      compElegivel = !isEditMode && validacao.elegivel;

      if (validacao.motivos.length > 0) {
        const acao = await aguardarDecisaoRegras(validacao.motivos);
        if (acao === "editar") {
          setCamposDestacados(
            validacao.motivos.map((m) => m.campo).filter(Boolean)
          );
          // Mensagem vazia: o PublishButton volta ao idle sem alert — o
          // usuário fica no formulário com os campos destacados.
          throw new Error("");
        }
        if (acao !== "salvar") throw new Error("Registro cancelado.");
      }
      setCamposDestacados([]);
    }

    // Snapshot SÍNCRONO antes de qualquer await
    const snapBefore = {
      level: vitaDataRef.current?.XP_VITA?.LEVEL ?? 1,
      currentLevelXp: vitaDataRef.current?.XP_VITA?.CURRENT_LEVEL_XP ?? 0,
      nextLevelXp: vitaDataRef.current?.XP_VITA?.NEXT_LEVEL_XP ?? 100,
    };

    const oldPath = isEditMode ? editingCheckin?.photo_path : "";
    const { photo_url, photo_path } = await uploadPhotoIfNeeded();

    if (isEditMode && capturedImage && oldPath && oldPath !== photo_path) {
      try {
        await deleteObject(ref(storage, oldPath));
      } catch (e) {
        console.warn("Falha ao remover foto antiga:", e);
      }
    }

    // gymCode ativo se o usuário está AGORA dentro do raio da academia.
    // Precisa ir no payload do createTreinoCheckin para a validação geo direta.
    const activeGymCode = GeofenceService.isCurrentlyInsideAcademy();

    if (!requestId.current)
      requestId.current =
        globalThis.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    /* Caloria SEMPRE preenchida no que vai para o banco: quem não digitou
       kcal leva a estimativa (MET × peso × horas) da duração informada. É o
       que a barra "Calorias da semana" soma — com 0 aqui, o dia de treino
       ficava aceso e a semana somava nada. */
    const atividadesFinais = atividades.map((a) => {
      if (a.calories_manual || Number(a.calories) > 0) return a;
      const { kcal } = estimarCalorias({
        key: a.activity_key,
        durationMin: a.duration_min,
        pesoKg,
      });
      return { ...a, calories: kcal };
    });
    const payload = {
      idempotency_key: requestId.current,
      title,
      description,
      location,
      checkinAtISO: new Date(checkinAt).toISOString(),
      photo_url,
      photo_path,
      atividades: atividadesFinais,
      activeGymCode,
      // Phase D: só true quando veio do treino AO VIVO (server aplica o bônus).
      live_workout: liveWorkoutRef.current === true,
    };

    const result = isEditMode
      ? await callFirebaseFunction("updateTreinoCheckin", {
          ...payload,
          id: editingCheckin.id,
        })
      : await callFirebaseFunction("createTreinoCheckin", payload);

    // VitaCoin: o servidor acabou de calcular o saldo e devolveu em `moeda`.
    // Só atualiza o espelho local — não pergunta de novo. `null` quando a flag
    // está off, quando o gate de 40min/foto bloqueou ou quando o teto do dia
    // já estava cheio; nesses casos nada acontece na tela, como deve ser.
    aplicarGanho(result?.moeda);

    const _totalDuration = atividades.reduce(
      (acc, a) => acc + (Number(a.duration_min) || 0),
      0
    );
    logWorkoutCompleted(_totalDuration, atividades.length, atividades.length);

    // ── Competição: cria o evento PENDENTE após o registro (best-effort;
    // falha aqui NUNCA desfaz o check-in — o backend valida tudo de novo).
    // O retorno traz os PONTOS reais (já com bônus fixo e impulso da equipe) —
    // é o que a animação de nível mostra. O app não recalcula isso.
    let compPontos = 0;
    let compImpulso = null;
    if (compElegivel && result?.id) {
      try {
        const uidComp = user?.uid || getAuth().currentUser?.uid;
        const eventoId = `TREINO_${result.id}`;
        const compPath = `competicao_fotos/${uidComp}/${eventoId}.jpg`;
        await uploadString(ref(storage, compPath), capturedImage, "data_url");
        const evento = await criarEventoCompeticao({
          tipo: "TREINO",
          origemId: result.id,
          tipoAtividadeId: compTipoAtividade,
          duracaoMin: compDuracao,
          isLive: liveWorkoutRef.current === true,
          fotoPath: compPath,
        });
        if (evento?.elegivel === false) {
          console.warn("[competicao] evento não pontuou:", evento?.motivo);
        } else {
          compPontos = Number(evento?.pontos) || 0;
          compImpulso = Number(evento?.impulso_mult) || null;
        }
      } catch (e) {
        console.warn(
          "[competicao] falha ao criar evento (registro preservado):",
          e?.message
        );
      }
    }

    // Ação concluída → o servidor começa a decidir o intersticial enquanto a
    // celebração anima. O anúncio em si só entra no slot depois dela
    // (PassoPreOfensiva → depoisDaAcao, que também serve a campanha própria).
    // O motor antigo (`registrarAcaoAd`) saiu daqui: ele abria o anúncio NA
    // HORA do salvar, por cima da contagem de XP.
    if (!isEditMode) anteciparDecisao("TREINO");

    // console.log("[handlePublish] result da CF:", result);
    // console.log("[handlePublish] snapBefore:", snapBefore);

    // ← xp_earned é o campo que a CF retorna. Em EDIÇÃO o que o usuário
    // "ganhou" é o efeito líquido (xp_delta, pode ser 0 ou negativo — a
    // animação só mostra ganhos positivos).
    const xpGained = isEditMode
      ? Math.max(0, result?.xp_delta ?? 0)
      : result?.xp_earned ??
        result?.xpGained ??
        result?.xp_gained ??
        result?.xpEarned ??
        0;
    if (xpGained > 0) logXpGained(xpGained, "treino_checkin", "treino");

    // Preserva o registro para o prompt 2f/2h antes que onDone limpe o form.
    // Edições não disparam convite de compartilhamento pós-registro.
    pendingShareRef.current = isEditMode
      ? null
      : {
          id: result?.id || null,
          title: title.trim(),
          description,
          location,
          checkin_at: checkinAt,
          photo_url,
          atividades: atividadesFinais.map((atividade) => ({ ...atividade })),
          points_total: atividades.reduce(
            (sum, atividade) => sum + (Number(atividade?.points) || 0),
            0
          ),
          xp_earned: xpGained,
          streak:
            result?.streak ?? vitaDataRef.current?.XP_VITA?.STREAK_DIAS ?? 0,
        };

    // Mesma progressão do servidor (functions/vita/xpVita.getXpProgress).
    // Abrange ganhos que atravessam mais de um nível.
    let newLevel = snapBefore.level;
    let newCurrentLevelXp = snapBefore.currentLevelXp + xpGained;
    let newNextLevelXp = Math.max(100, snapBefore.nextLevelXp);
    while (newCurrentLevelXp >= newNextLevelXp) {
      newCurrentLevelXp -= newNextLevelXp;
      newLevel += 1;
      newNextLevelXp += 50;
    }
    const leveledUp = newLevel > snapBefore.level;
    // A conta acima é só RESERVA (servidor antigo sem `levelUp`): o nível que
    // a tela mostra vem do servidor, calculado na transação do XP.
    const nivelServidor = celebracaoDoServidor(result?.levelUp, {
      newLevel, prevLevel: snapBefore.level, leveledUp,
      prevCurrentLevelXp: snapBefore.currentLevelXp,
      prevNextLevelXp: snapBefore.nextLevelXp, newCurrentLevelXp, newNextLevelXp,
    });

    // Dados do streak vindos da CF (createTreinoCheckin) p/ a StreakScreen.
    // No modo edição a CF não retorna streak → fica null e a tela não abre.
    streakDataRef.current = {
      streak: result?.streak ?? null,
      weekActiveDays: result?.weekActiveDays ?? [], // índices 0-6 já defendidos
      todayIdx: result?.todayIdx ?? new Date().getDay(),
      // Gate diário vindo da CF: true só na 1ª atividade do dia (foguinho acende).
      celebrationEligible: result?.STREAK_CELEBRATION_ELIGIBLE === true,
    };

    return {
      streak: result?.streak ?? 0,
      // VitaCoin creditada pelo servidor (null sem crédito) — a comemoração
      // conta e toca o som de moeda.
      moeda: result?.moeda || null,
      // Clubes/desafios em que pontuou ([{clubeId, nome, tipo, pontos}]).
      clubes: Array.isArray(result?.clubes) ? result.clubes : [],
      xpGained,
      ...nivelServidor,
      levelUp: isEditMode ? null : result?.levelUp || null,
      // Competição: pontos REAIS do evento (servidor) e o total anterior do
      // último placar conhecido — a animação sobe de um para o outro.
      pontosGanhos: compPontos,
      pontosAntes: compPontos > 0 ? lerMeusPontos() : 0,
      impulsoMult: compImpulso,
      /* Sem competição o bloco de pontos sumia da comemoração, e quem está de
         fora nunca via o que estava deixando na mesa. Agora ele aparece com a
         PREVISÃO e o convite — e estes dois campos são o que distingue "não
         estou em clube nenhum" de "estou, mas este registro não pontuou"
         (duração curta, sem foto, sem ticket), que continua sem bloco. */
      semCompeticao: !compAtiva,
      pontosPrevistos: compAtiva
        ? 0
        : pontosPrevistos({ tipo: "TREINO", xp: xpGained }),
    };
  };

  return (
    <Offcanvas.Redesign>
      {/* Botão + fixo — só quando explicitamente pedido (o FAB padrão vive no
          TreinoUser, que dispara provider.openCheckin). */}
      {!isEditMode && showFab && (
        <button
          className={Style.floatingAddBtn}
          onClick={openCreateFlow}
          aria-label="Adicionar check-in"
          data-tutorial-id="treino-btn-checkin"
        >
          <FaPlus />
        </button>
      )}

      <Offcanvas
        show={showCheckinOff}
        onHide={() => {
          if (saving) return;
          // Em edição não há câmera atrás: voltar é sair da edição.
          if (isEditMode) {
            setShowCheckinOff(false);
            onCloseEdit?.();
            return;
          }
          /* Gesto de borda ou botão voltar do Android, em QUALQUER passo:
             antes fechava o check-in inteiro e jogava a pessoa no início da
             aba, perdendo tudo. Agora pergunta — e quem confirma volta para a
             CÂMERA, que é de onde o check-in nasce. */
          setConfirmarVoltar(true);
        }}
        placement="end"
        className={Style.offCanvas}
      >
        <Offcanvas.Header className={Style.offHeader}>
          <button
            className={Style.btnsHeaderOff}
            aria-label="Voltar"
            disabled={saving}
            onClick={() => {
              if (formStep === 2) {
                setFormStep(1);
                return;
              }
              // Em edição, voltar é sair da edição — não há câmera atrás.
              if (isEditMode) {
                setShowCheckinOff(false);
                onCloseEdit?.();
                return;
              }
              setConfirmarVoltar(true);
            }}
          >
            ‹
          </button>
          <Offcanvas.Title className={Style.OffcanvasTitle}>
            {isEditMode ? "Editar check-in" : "Check-in treino"}
          </Offcanvas.Title>
          <span className={Style.step}>{formStep}/2</span>
        </Offcanvas.Header>
        <Offcanvas.Body className={Style.offBody}>
          <fieldset
            key={formStep}
            disabled={saving}
            className={Style.stepContent}
          >
            {formStep === 1 && (
              <>
                <div
                  className={Style.photoWrap}
                  style={destacado("foto") ? destaqueStyle : undefined}
                >
                  {capturedImage || editingCheckin?.photo_url ? (
                    <img
                      className={Style.photoPreview}
                      src={capturedImage || editingCheckin?.photo_url}
                      alt="Foto do check-in"
                    />
                  ) : (
                    <div className={Style.photoEmpty}>
                      Adicione uma foto do treino
                    </div>
                  )}
                  <span className={Style.photoDate}>
                    {dayjs(checkinAt).format("DD/MM · HH:mm")}
                  </span>
                  <button
                    className={Style.editPhotoBtn}
                    onClick={openChangePhoto}
                    aria-label="Alterar foto"
                  >
                    <FaPencilAlt />
                  </button>
                </div>
                {destacado("foto") && (
                  <p className={Style.warning}>
                    Tire uma foto para o treino pontuar.
                  </p>
                )}
                <div className={Style.formBlock}>
                  <label className={Style.formGroup}>
                    Título
                    <input
                      {...campoTocavel}
                      className={Style.inputCamp}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Peito e tríceps"
                    />
                  </label>
                  <label className={Style.formGroup}>
                    Descrição · opcional
                    <textarea
                      {...campoTocavel}
                      className={Style.inputCamp}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Como foi o treino hoje?"
                      rows={2}
                    />
                  </label>
                  <label className={Style.formGroup}>
                    Data e hora
                    <input
                      {...campoTocavel}
                      className={Style.inputCamp}
                      type="datetime-local"
                      value={checkinAt}
                      onChange={(e) => setCheckinAt(e.target.value)}
                    />
                  </label>
                  <label className={Style.formGroup}>
                    Local · opcional
                    <input
                      {...campoTocavel}
                      className={Style.inputCamp}
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Onde você treinou?"
                    />
                  </label>
                </div>
              </>
            )}
            {formStep === 2 && (
              <div className={Style.intro}>
                <strong>{title}</strong>
                <span>
                  {dayjs(checkinAt).format("DD/MM · HH:mm")} · Confira as
                  informações do treino
                </span>
              </div>
            )}
            <div
              className={Style.activitiesBox}
              style={
                destacado("duracao") || destacado("modalidade")
                  ? destaqueStyle
                  : undefined
              }
            >
              <div className={Style.activitiesHeader}>
                <h5>Atividades</h5>
                <span>{atividades.length} selecionadas</span>
              </div>
              {/* O botão vem ANTES da lista: no passo 2 cada atividade abre
                  um bloco alto de métricas, e no fim da lista o botão ficava a
                  dois ou três rolares de distância — para adicionar a segunda
                  atividade era preciso passar por toda a primeira. */}
              <button className={Style.addActivityBtn} onClick={openPicker}>
                <FaPlus />{" "}
                {atividades.length
                  ? "Adicionar atividade"
                  : "Selecionar atividade"}
              </button>
              {atividades.map((a, idx) => {
                const preset = getActivityMusclePreset(a.activity_key);
                const muscles = sanitizeActiveMuscles(a.active_muscles);
                return (
                  <div
                    key={`${a.activity_key}-${idx}`}
                    className={Style.activityEntry}
                  >
                    <div className={Style.activityRow}>
                      <div className={Style.activityInfo}>
                        <strong>{a.activity_label}</strong>
                        {formStep === 1 && (
                          <span>Informações no próximo passo</span>
                        )}
                      </div>
                      <button
                        className={Style.removeInline}
                        aria-label={`Remover ${a.activity_label}`}
                        onClick={() =>
                          setAtividades((current) =>
                            current.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                    {formStep === 2 && (
                      <>
                        <div className={Style.metrics}>
                          {[
                            ["duration_min", "Duração (min)"],
                            ["calories", "Calorias (kcal)"],
                            ["distance_km", "Distância (km)"],
                            ["steps", "Passos"],
                          ].map(([field, label]) => (
                            <label className={Style.formGroup} key={field}>
                              {label}
                              <input
                                {...campoTocavel}
                                type="text"
                                inputMode={
                                  field === "distance_km"
                                    ? "decimal"
                                    : "numeric"
                                }
                                pattern={
                                  field === "distance_km"
                                    ? "[0-9]*[.,]?[0-9]*"
                                    : "[0-9]*"
                                }
                                className={Style.inputCamp}
                                value={lerRascunho(idx, field, a[field])}
                                placeholder="0"
                                autoComplete="off"
                                onChange={(e) =>
                                  digitarNumero(idx, field, e.target.value)
                                }
                                onBlur={() => encerrarRascunho(idx, field)}
                              />
                              {field === "calories" && !a.calories_manual && (
                                <span className={Style.dicaCampo}>
                                  {Number(a.duration_min) > 0
                                    ? `estimado pela duração${pesoKg ? "" : " · sem avaliação, usando 70 kg"}`
                                    : "informe a duração para estimar"}
                                </span>
                              )}
                            </label>
                          ))}
                          <label className={Style.formGroup}>
                            Hora de início
                            <input
                              {...campoTocavel}
                              className={Style.inputCamp}
                              type="time"
                              value={a.start_time || ""}
                              onChange={(e) =>
                                updateActivity(idx, {
                                  start_time: e.target.value,
                                })
                              }
                            />
                          </label>
                        </div>
                        <div className={Style.muscleFieldHeader}>
                          <span>Grupos musculares</span>
                          <span>{muscles.length} selecionados</span>
                        </div>
                        <div className={Style.muscleChips}>
                          {CHECKIN_MUSCLES.filter(
                            (m) =>
                              preset.mode !== "fixed" || muscles.includes(m.key)
                          ).map((m) => (
                            <button
                              type="button"
                              key={m.key}
                              className={`${Style.muscleChip} ${
                                muscles.includes(m.key)
                                  ? Style.muscleChipSelected
                                  : ""
                              }`}
                              disabled={preset.mode === "fixed"}
                              aria-pressed={muscles.includes(m.key)}
                              onClick={() =>
                                updateActivity(idx, {
                                  active_muscles: muscles.includes(m.key)
                                    ? muscles.filter((k) => k !== m.key)
                                    : [...muscles, m.key],
                                })
                              }
                            >
                              {m.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {formStep === 1 ? (
              <button
                className={Style.confirmAddBtn}
                disabled={!title.trim() || !atividades.length}
                onClick={() => setFormStep(2)}
              >
                Continuar
              </button>
            ) : (
              <PublishButton
                disabled={!title.trim() || !atividades.length}
                onPublish={handlePublish}
                onBusyChange={setSaving}
                onDone={(celebration) => {
                  const sharePayload = pendingShareRef.current;
                  const eraEdicao = isEditMode;
                  setShowCheckinOff(false);
                  if (onCloseEdit) onCloseEdit();
                  // Atualiza o feed JÁ (usuário vê a lista mudar durante os 5s)
                  if (onSaved) onSaved();
                  resetAll();
                  moedaFimRef.current = eraEdicao ? null : celebration?.moeda || null;
                  // Monetização: LevelUp (se subiu) → intersticial → segue.
                  const seguir = () => {

                  // CF pode não ter sido redeployada ainda → lê do vitaDataRef
                  // (onSnapshot já atualizou enquanto o overlay de XP estava aberto)
                  const cfData = streakDataRef.current;
                  const liveStreak =
                    vitaDataRef.current?.XP_VITA?.STREAK_DIAS ?? 0;
                  const effectiveStreak =
                    (cfData?.streak ?? 0) > 0 ? cfData.streak : liveStreak;

                  // Gate diário: só mostra a ofensiva quando a CF marcou elegível
                  // (1ª atividade do dia). Sem o flag explícito (ex.: fallback pelo
                  // vitaData) NÃO mostra — evita "aparecer toda vez". Sem delay: abre
                  // direto aqui (onDone = overlay de nível já fechou/transição fim).
                  if (
                    celebration?.preview ||
                    (effectiveStreak > 0 &&
                      cfData?.celebrationEligible === true)
                  ) {
                    const celebData = {
                      streak: celebration?.preview
                        ? Math.max(7, effectiveStreak)
                        : effectiveStreak,
                      weekActiveDays: cfData?.weekActiveDays ?? [],
                      todayIdx: cfData?.todayIdx ?? new Date().getDay(),
                    };
                    streakDataRef.current = celebData;
                    setShowStreak(true);
                  } else if (sharePayload) {
                    setPostWorkoutShare(sharePayload);
                    pendingShareRef.current = null;
                  } else {
                    abrirVitaCoins();
                  }
                  };
                  if (eraEdicao) seguir();
                  else setPreOfensiva({ levelUp: celebration?.levelUp, seguir });
                }}
                label={isEditMode ? "Salvar check-in" : "Check-in treino"}
              />
            )}
          </fieldset>
          {isEditMode && (
            <button
              type="button"
              className={Style.shareFab}
              onClick={() => setShareRegistrado(true)}
              aria-label="Compartilhar treino"
              disabled={saving}
            >
              <FiShare2 />
            </button>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Compartilhar treino registrado — mesmo carrossel do pós-registro. */}
      <Offcanvas
        show={shareRegistrado && isEditMode}
        onHide={() => setShareRegistrado(false)}
        placement="end"
        className={Style.offCanvas}
      >
        <Offcanvas.Body className={Style.shareBody}>
          {shareRegistrado && editingCheckin && (
            <React.Suspense fallback={<p style={{ padding: 20 }}>Preparando imagem…</p>}>
              <ShareRefeicao
                type="workout"
                workout={editingCheckin}
                profileData={profileData}
                onBack={() => setShareRegistrado(false)}
              />
            </React.Suspense>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Offcanvas 2: Picker */}
      <Offcanvas
        show={showPickerOff}
        onHide={() => {
          setShowPickerOff(false);
          setActivitySearch("");
        }}
        placement="end"
        className={Style.offCanvas}
      >
        <Offcanvas.Header className={Style.offHeader}>
          <button
            onClick={() => {
              setShowPickerOff(false);
              setActivitySearch("");
            }}
            className={`${Style.btnsHeaderOff} ${Style.btnBack}`}
          >
            <svg
              width="31"
              height="19"
              viewBox="0 0 31 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={Style.backArrow}
            >
              <path
                d="M17.1799 17.6508L30.1086 4.66082C30.3231 4.44747 30.4932 4.19375 30.6091 3.91432C30.725 3.63489 30.7845 3.33526 30.784 3.03274C30.7836 2.73021 30.7232 2.43077 30.6065 2.15168C30.4897 1.87259 30.3189 1.61938 30.1038 1.40666C29.6738 0.980473 29.0926 0.74176 28.4871 0.742658C27.8817 0.743555 27.3012 0.98399 26.8725 1.41145L15.431 12.7722L4.07045 1.44525C3.64045 1.01907 3.05927 0.780353 2.45384 0.78125C1.84842 0.782148 1.26795 1.02258 0.839207 1.45004C0.622987 1.66261 0.451097 1.91597 0.333498 2.19544C0.215899 2.47491 0.154931 2.77494 0.154122 3.07815C0.15583 3.38135 0.217687 3.68119 0.336114 3.96032C0.454541 4.23944 0.627181 4.49229 0.844031 4.70421L13.8112 17.6558C14.0261 17.8881 14.2868 18.0734 14.5769 18.1999C14.867 18.3265 15.1801 18.3916 15.4966 18.3911C15.8131 18.3906 16.1261 18.3246 16.4158 18.1972C16.7055 18.0698 16.9657 17.8838 17.1799 17.6508Z"
                fill="currentColor"
              />
            </svg>
          </button>
          <Offcanvas.Title className={Style.OffcanvasTitle}>
            Selecione uma atividade
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body
          className={Style.offBody}
          ref={pickerBodyRef}
          onScroll={handlePickerScroll}
        >
          {/* Search */}
          <div className={Style.searchWrap}>
            <span className={Style.searchIcon}>🔍</span>
            <Form.Control
              {...campoTocavel}
              className={Style.searchInput}
              placeholder="Buscar atividade…"
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
            />
            {activitySearch && (
              <button
                className={Style.searchClear}
                onClick={() => setActivitySearch("")}
                aria-label="Limpar busca"
              >
                ✕
              </button>
            )}
          </div>

          {/* Resultados */}
          {activitySearch.trim()
            ? (() => {
                const q = activitySearch.trim().toLowerCase();
                const results = ACTIVITIES.filter(
                  (a) =>
                    a.label.toLowerCase().includes(q) ||
                    a.category.toLowerCase().includes(q)
                );
                return results.length > 0 ? (
                  <BoxShadow className={Style.categoryBox}>
                    {results.map((act) => (
                      <button
                        key={act.key}
                        className={Style.pickItem}
                        onClick={() => {
                          handleSelectActivity(act);
                          setActivitySearch("");
                        }}
                      >
                        <span className={Style.pickItemEmoji}>{act.emoji}</span>
                        {act.label}
                      </button>
                    ))}
                  </BoxShadow>
                ) : (
                  <p className={Style.searchEmpty}>
                    Nenhuma atividade encontrada para "{activitySearch}"
                  </p>
                );
              })()
            : ACTIVITY_CATEGORIES_ORDER.filter(
                (cat) => (ACTIVITIES_BY_CATEGORY[cat] || []).length > 0
              ).map((cat) => (
                <BoxShadow key={cat} className={Style.categoryBox}>
                  <h5 className={Style.categoryTitle}>{cat}</h5>
                  {(ACTIVITIES_BY_CATEGORY[cat] || []).map((act) => (
                    <button
                      key={act.key}
                      className={Style.pickItem}
                      onClick={() => handleSelectActivity(act)}
                    >
                      <span className={Style.pickItemEmoji}>{act.emoji}</span>
                      {act.label}
                    </button>
                  ))}
                </BoxShadow>
              ))}

          {/* Botão voltar ao topo */}
          <button
            className={`${Style.scrollTopBtn} ${
              showScrollTop ? Style.scrollTopVisible : ""
            }`}
            onClick={scrollPickerToTop}
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Aviso das regras (v3.7.3 — Bloco E): lista TODOS os motivos de
          não-pontuação; o registro nunca é bloqueado, só a pontuação. */}
      {/* `enforceFocus`/`restoreFocus` desligados pelo mesmo motivo do
          SwipeableOffcanvas: estes Modal entram na MESMA pilha do
          ModalManager, e enquanto um sai ele ainda é o do topo — um toque num
          campo do formulário atrás, nesse intervalo, perdia o foco e o
          teclado não abria. */}
      <Modal
        className={Style.rulesModal}
        show={confirmarVoltar}
        onHide={() => setConfirmarVoltar(false)}
        enforceFocus={false}
        restoreFocus={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 17 }}>Calma aí, o treino ainda não foi salvo 👀</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 14.5, lineHeight: 1.5 }}>
          Se você sair agora, a gente descarta o que você preencheu e te leva
          de volta pra câmera. Bora terminar o check-in? São só alguns toques.
        </Modal.Body>
        <Modal.Footer style={{ flexWrap: "wrap", gap: 8 }}>
          <Button variant="outline-secondary" onClick={() => setConfirmarVoltar(false)}>
            Continuar aqui
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setConfirmarVoltar(false);
              resetAll();
              setShowCheckinOff(false);
              setShowCustomCamera(true);
            }}
          >
            Voltar pra câmera
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        className={Style.rulesModal}
        show={!!compWarning}
        onHide={() => responderAvisoRegras("cancelar")}
        enforceFocus={false}
        restoreFocus={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 17 }}>
            Este treino não vai pontuar
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 14.5, lineHeight: 1.5 }}>
          <ul style={{ paddingLeft: 18, margin: 0, display: "grid", gap: 8 }}>
            {(compWarning?.motivos || []).map((m, i) => (
              <li key={`${m.motivo}-${i}`}>{m.mensagem}</li>
            ))}
          </ul>
          <p style={{ margin: "12px 0 0", fontSize: 13, color: "#6b7280" }}>
            Sua ofensiva (foguinho) conta normalmente. Deseja continuar?
          </p>
        </Modal.Body>
        <Modal.Footer style={{ flexWrap: "wrap", gap: 8 }}>
          <Button
            variant="outline-secondary"
            onClick={() => responderAvisoRegras("editar")}
          >
            Ver e editar
          </Button>
          <Button
            variant="primary"
            onClick={() => responderAvisoRegras("salvar")}
          >
            Salvar assim mesmo
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Aviso de localização em contexto (v3.7.3 — Bloco C): no máximo 1x a
          cada 14 dias, e só ao abrir um novo registro de treino. */}
      {geoBannerStatus && (
        <GeoPermissionDeniedBanner
          permissionStatus={geoBannerStatus}
          onDismiss={() => setGeoBannerStatus(null)}
        />
      )}

      {/* Galeria escondida só ao CRIAR um registro em competição: é a foto
          que vira a prova do evento, e ela tem de ser tirada na hora. Ao
          EDITAR (trocar ou pôr a foto de um treino já registrado — o caso de
          toda atividade gravada por GPS, que nasce sem foto), a câmera é a
          mesma de qualquer outro lugar do app, com galeria. A foto nova ainda
          passa pelo dono: a edição gera uma versão PENDENTE do evento na fila
          de verificação. */}
      {showCustomCamera && (
        <CustomCamera
          mode="checkin"
          allowGallery={!compAtiva || isEditMode}
          onCapture={(dataUrl) => {
            pendingLiveCheckinRef.current = false;
            setCapturedImage(dataUrl);
            setShowCheckinOff(true);
            setShowCustomCamera(false);
          }}
          onClose={() => {
            setShowCustomCamera(false);
            // Treino ao vivo finalizado: mesmo sem foto, segue pro check-in
            // pré-preenchido. No fluxo manual, fechar a câmera apenas cancela.
            if (pendingLiveCheckinRef.current) {
              pendingLiveCheckinRef.current = false;
              setShowCheckinOff(true);
            }
          }}
        />
      )}

      {showStreak && (
        <StreakScreen
          redesign
          streak={streakDataRef.current.streak}
          weekActiveDays={streakDataRef.current.weekActiveDays}
          frozenDays={frozenWeekIndices(vitaData?.FROZEN_DAYS)}
          todayIdx={streakDataRef.current.todayIdx}
          onClose={() => {
            setShowStreak(false);
            if (pendingShareRef.current) {
              setPostWorkoutShare(pendingShareRef.current);
              pendingShareRef.current = null;
            } else {
              abrirVitaCoins();
            }
          }}
        />
      )}

      {postWorkoutShare && (
        <WorkoutShare
          workout={postWorkoutShare}
          profileData={profileData}
          onClose={() => {
            setPostWorkoutShare(null);
            abrirVitaCoins();
          }}
        />
      )}

      {preOfensiva && (
        <PassoPreOfensiva
          levelUp={preOfensiva.levelUp}
          gatilho="TREINO"
          onFim={() => {
            const { seguir } = preOfensiva;
            setPreOfensiva(null);
            seguir();
          }}
        />
      )}

      {vitaCoinsFim && (
        <PassoVitaCoins moeda={vitaCoinsFim} onFim={() => setVitaCoinsFim(null)} />
      )}
    </Offcanvas.Redesign>
  );
};

export default RegistrarTreinoCheckin;
